import React from 'react';
import PropTypes from 'prop-types';
import Favicon from 'react-favicon';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';

import auth from '../../services/appAuth';
import { SYSTEM_RESOURCE } from '../../constants/EndPoints';
import * as misc from '../../constants/Misc';

import './Login.scss';

const PASSWORD_MIN_LENGTH = 12;

function parseQuery(location) {
  const query = (location && location.query) || {};
  const search = (location && location.search) || '';
  const params = new URLSearchParams(search);

  return {
    oobCode: query.oobCode || params.get('oobCode') || '',
    continueUrl: query.continueUrl || params.get('continueUrl') || '',
  };
}

function validPasswordLength(password) {
  return /^(?=.{12})/.test(password);
}

function validPasswordLowercase(password) {
  return /^(?=.*[a-z])/.test(password);
}

function validPasswordUppercase(password) {
  return /^(?=.*[A-Z])/.test(password);
}

function validPasswordContainNumber(password) {
  return /^(?=.*[0-9])/.test(password);
}

function getPasswordError(password) {
  if (!validPasswordLength(password)) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
  }

  if (!validPasswordLowercase(password)) {
    return 'Password must include at least one lowercase letter.';
  }

  if (!validPasswordUppercase(password)) {
    return 'Password must include at least one uppercase letter.';
  }

  if (!validPasswordContainNumber(password)) {
    return 'Password must include at least one number.';
  }

  return '';
}

function friendlyError(error) {
  switch (error && error.code) {
    case 'auth/expired-action-code':
      return 'This reset link expired. Please request a new password reset email.';
    case 'auth/invalid-action-code':
      return 'This reset link is invalid or has already been used.';
    case 'auth/user-disabled':
      return 'This account is currently disabled.';
    case 'auth/weak-password':
      return 'Please choose a stronger password.';
    default:
      return (
        (error && error.message) ||
        'We could not complete the password reset for this link.'
      );
  }
}

function safeContinuePath(rawTarget) {
  if (!rawTarget) {
    return '/login';
  }

  try {
    const url = new URL(rawTarget, window.location.origin);
    if (url.origin !== window.location.origin) {
      return '/login';
    }
    return url.pathname + url.search + url.hash;
  } catch (error) {
    return '/login';
  }
}

function redirectTo(path, props) {
  if (props && props.history && typeof props.history.replace === 'function') {
    props.history.replace(path);
    return;
  }

  window.location.replace(path);
}

function ResetPasswordConfirm(props) {
  const [status, setStatus] = React.useState('loading');
  const [error, setError] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [passwordAgain, setPasswordAgain] = React.useState('');
  const [showPasswords, setShowPasswords] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState('');
  const [passwordAgainError, setPasswordAgainError] = React.useState('');
  const location = (props && props.location) || window.location;
  const query = parseQuery(location);
  const sys = SYSTEM_RESOURCE;
  const systemName = process.env.REACT_APP_SYSTEM_NAME;
  const backgroundImagePath = require(`../../../images/system/${sys}/login_bg.png`);

  const mainStyle = {
    backgroundImage: `url(${backgroundImagePath})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  React.useEffect(function () {
    document.title =
      (process.env.REACT_APP_SYSTEM_NAME || 'AQS') + ' - Reset password';
  }, []);

  React.useEffect(
    function () {
      let cancelled = false;

      async function loadCode() {
        if (!query.oobCode) {
          setStatus('error');
          setError('This reset link is missing its action code.');
          return;
        }

        setStatus('loading');
        setError('');

        try {
          const resolvedEmail = await auth.verifyPasswordResetCode(
            query.oobCode
          );
          if (cancelled) {
            return;
          }

          setEmail(resolvedEmail || '');
          setStatus('ready');
        } catch (err) {
          if (cancelled) {
            return;
          }

          setStatus('error');
          setError(friendlyError(err));
        }
      }

      loadCode();

      return function () {
        cancelled = true;
      };
    },
    [query.oobCode]
  );

  React.useEffect(
    function () {
      if (!password) {
        setPasswordError('');
        return;
      }

      setPasswordError(getPasswordError(password));
    },
    [password]
  );

  React.useEffect(
    function () {
      if (!passwordAgain) {
        setPasswordAgainError('');
        return;
      }

      setPasswordAgainError(
        password === passwordAgain ? '' : 'Passwords do not match.'
      );
    },
    [password, passwordAgain]
  );

  const continuePath = safeContinuePath(query.continueUrl);

  const handleSubmit = async function () {
    const nextPasswordError = getPasswordError(password);
    const nextPasswordAgainError =
      password === passwordAgain ? '' : 'Passwords do not match.';

    setPasswordError(nextPasswordError);
    setPasswordAgainError(nextPasswordAgainError);
    setError('');

    if (nextPasswordError || nextPasswordAgainError) {
      return;
    }

    setStatus('applying');

    try {
      await auth.confirmPasswordReset(query.oobCode, password);
      setStatus('success');
    } catch (err) {
      setStatus('ready');
      setError(friendlyError(err));
    }
  };

  return (
    <section className='page login-page mfa-confirm-removal-page'>
      <Favicon url={require(`./../../../images/system/${sys}/favicon_1.png`)} />

      <div className='main' style={mainStyle}>
        <div className='login-form form-horizontal'>
          <header>
            <div className='logo'>{systemName}</div>
            <div className='version'>
              {`v.`} <strong>{misc.versionNumber}</strong>
            </div>
          </header>

          <h2 className='mfa-confirm-title'>Reset password</h2>

          {status === 'loading' && (
            <div className='mfa-confirm-loading'>
              <CircularProgress size={24} />
              <span>Checking this reset link...</span>
            </div>
          )}

          {status !== 'loading' && error && (
            <Alert severity='error' className='mfa-confirm-alert'>
              {error}
            </Alert>
          )}

          {status === 'ready' && (
            <>
              <p className='mfa-confirm-copy'>
                Choose a new password for:
                <br /> <strong>{email}</strong>.
              </p>
              <div className='mfa-confirm-actions' style={{ display: 'block' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: -20,
                  }}
                >
                  <IconButton
                    aria-label={
                      showPasswords ? 'Hide passwords' : 'Show passwords'
                    }
                    onClick={() => setShowPasswords((current) => !current)}
                  >
                    {showPasswords ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </div>
                <TextField
                  type={showPasswords ? 'text' : 'password'}
                  label='New password'
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  error={Boolean(passwordError)}
                  helperText={passwordError || ' '}
                  fullWidth
                  margin='dense'
                />
                <TextField
                  type={showPasswords ? 'text' : 'password'}
                  label='Confirm new password'
                  value={passwordAgain}
                  onChange={(event) => setPasswordAgain(event.target.value)}
                  error={Boolean(passwordAgainError)}
                  helperText={passwordAgainError || ' '}
                  fullWidth
                  margin='dense'
                />
                <Button
                  type='button'
                  variant='contained'
                  onClick={handleSubmit}
                >
                  Change password
                </Button>
                <div
                  style={{
                    fontSize: 10,
                    maxWidth: 200,
                    marginLeft: 150,
                    marginTop: -30,
                  }}
                >
                  Password must be <strong>12 characters</strong> long,
                  <br />
                  contain one <strong>uppercase letter</strong>,
                  <br />
                  one <strong>number</strong>, and one{' '}
                  <strong>special symbol</strong>.
                </div>
              </div>
            </>
          )}

          {status === 'applying' && (
            <div className='mfa-confirm-loading'>
              <CircularProgress size={24} />
              <span>Saving your new password...</span>
            </div>
          )}

          {status === 'success' && (
            <>
              <Alert severity='success' className='mfa-confirm-alert'>
                Your password has been changed successfully.
              </Alert>
              <p className='mfa-confirm-copy'>
                You can now return to the login page and sign in with your new
                password.
              </p>
              <div className='mfa-confirm-actions'>
                <Button
                  type='button'
                  variant='contained'
                  onClick={() => redirectTo(continuePath, props)}
                >
                  Continue to login
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <div className='mfa-confirm-actions'>
              <Button
                type='button'
                variant='contained'
                onClick={() => redirectTo(continuePath, props)}
              >
                Back to login
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

ResetPasswordConfirm.propTypes = {
  history: PropTypes.shape({
    replace: PropTypes.func,
  }),
  location: PropTypes.shape({
    query: PropTypes.object,
    search: PropTypes.string,
  }),
};

export default ResetPasswordConfirm;
